/**
 * PATH: apps/qa/src/pages/new-inspection/JobSelectStep.tsx
 * PURPOSE: Step 0 — job search and selection with AI pre-selection trigger
 */

import { Building2, Loader2, Plus, Search, Sparkles, X } from 'lucide-react'
import type { Job } from './inspection-types'
import { getSiteName, getClientName } from './ai-preselection'

interface JobSelectStepProps {
  selectedJob: Job | null
  jobSearch: string
  setJobSearch: (v: string) => void
  jobs: Job[]
  jobsLoading: boolean
  onSelectJob: (job: Job) => void
  onClearJob: () => void
  onNewJob?: () => void
  hasNextPage?: boolean
  fetchingNext?: boolean
  onLoadMore?: () => void
}

export default function JobSelectStep({
  selectedJob, jobSearch, setJobSearch, jobs, jobsLoading, onSelectJob, onClearJob, onNewJob,
  hasNextPage, fetchingNext, onLoadMore,
}: JobSelectStepProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground mb-2">Select a Job</h2>
          <p className="text-muted-foreground text-sm">
            Link this inspection to a job from your project list. The job details will be used for reporting.
          </p>
        </div>
        {onNewJob ? (
        <button
          type="button"
          onClick={onNewJob}
          className="inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-lg border border-input text-sm font-medium hover:bg-accent transition-colors"
        >
          <Plus className="w-4 h-4" />Job
        </button>
        ) : null}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search jobs by number, client, or site..."
          value={jobSearch}
          onChange={(e) => setJobSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
        />
      </div>

      {/* Selected Job */}
      {selectedJob && (
        <div className="p-4 rounded-xl bg-primary/10 border-2 border-primary/30 animate-in zoom-in-95 duration-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-foreground">
                Job #{selectedJob.job_id}
                {selectedJob.display_name ? (
                  <span className="ml-2 font-normal text-muted-foreground">{selectedJob.display_name}</span>
                ) : null}
                {selectedJob.type && (
                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/20 text-primary capitalize">
                    {selectedJob.type}
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                {getClientName(selectedJob.client) || 'No client'} • {getSiteName(selectedJob.site) || 'No site'}
              </p>
            </div>
            <button
              onClick={onClearJob}
              className="p-2 rounded-lg hover:bg-primary/20 text-primary transition-colors"
              aria-label="Remove selected job"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {selectedJob.tags && selectedJob.tags.length > 0 && (
            <div className="mt-3 pt-3 border-t border-primary/20">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Job tags detected (used for AI pre-selection)
              </p>
              <div className="flex flex-wrap gap-1">
                {selectedJob.tags.map((tag, idx) => (
                  <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Jobs List */}
      {!selectedJob && (
        <div className="max-h-[400px] overflow-y-auto space-y-2">
          {jobsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No jobs found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your search or check your job list
              </p>
            </div>
          ) : (
            jobs.map((job) => (
              <button
                key={job.job_id}
                onClick={() => onSelectJob(job)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-border hover:bg-accent hover:border-primary/50 active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                  <Building2 className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    Job #{job.job_id}
                    {job.display_name ? (
                      <span className="ml-2 font-normal text-muted-foreground">{job.display_name}</span>
                    ) : null}
                    {job.tags && job.tags.length > 0 && (
                      <span className="ml-2 text-xs font-normal text-primary">
                        {job.tags.slice(0, 2).join(', ')}{job.tags.length > 2 ? '...' : ''}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {getClientName(job.client) || 'No client'} • {getSiteName(job.site) || 'No site'}
                  </p>
                </div>
                {job.type && (
                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium capitalize">
                    {job.type}
                  </span>
                )}
                {!job.type && job.status && (
                  <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                    {job.status}
                  </span>
                )}
              </button>
            ))
          )}
          {hasNextPage ? (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={fetchingNext}
              className="flex min-h-[44px] w-full items-center justify-center rounded-lg border border-input text-sm font-medium hover:bg-accent md:min-h-0 md:py-2"
            >
              {fetchingNext ? 'Loading…' : 'Load more jobs'}
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}
